/// <amd-module name="Repos/PullRequestList/Components/PullRequestLabels" />
import * as React from "react";
import { IIntersectionContext, Intersection, IntersectionContext } from "VSSUI/Intersection";
import { WebApiTagDefinition } from "Tfs/Platform/Contracts/Core";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import { Pill, PillSize } from "VSSUI/Pill";
import { Tooltip } from "VSSUI/TooltipEx";

interface PullRequestLabelsProps {
    labels: WebApiTagDefinition[];
}

export class PullRequestLabels extends React.Component<PullRequestLabelsProps> {
    private intersectionContext: IIntersectionContext;
    private observedElements = new Map<HTMLElement, boolean>();

    public constructor(props: PullRequestLabelsProps) {
        super(props);
    }

    public render(): JSX.Element {
        const { labels } = this.props;

        const pillGroup = (
            <PillGroup className="margin-left-8" overflow={PillGroupOverflow.fade}>
                {labels.map(label => (
                    <Pill key={label.id} size={PillSize.compact}>
                        <Tooltip text={label.name} overflowOnly overflowDetected={this.overflowDetected}>
                            <span ref={element => this.observeElement(element)}>{label.name}</span>
                        </Tooltip>
                    </Pill>
                ))}
            </PillGroup>
        );

        return (
            <Intersection>
                <div style={{overflow: "hidden"}}>
                    <IntersectionContext.Consumer>
                        {intersectionContext => {
                            this.intersectionContext = intersectionContext;
                            return pillGroup;
                        }}
                    </IntersectionContext.Consumer>
                </div>
            </Intersection>
        );
    }

    public componentDidMount(): void {
        if (!this.intersectionContext) {
            return;
        }

        this.intersectionContext.register(this.onIntersect);
    }

    public componentWillUnmount(): void {
        if (!this.intersectionContext) {
            return;
        }

        this.intersectionContext.unregister(this.onIntersect);
        this.observedElements.forEach((_overflowDetected: boolean, element: HTMLElement) => {
            this.intersectionContext.unobserve(element);
        });
    }

    private observeElement = (element: HTMLElement | null): void => {
        if (!element || !this.intersectionContext || this.observedElements.has(element)) {
            return;
        }

        this.intersectionContext.observe(element);
        this.observedElements.set(element, false);
    };

    private onIntersect = (entries: IntersectionObserverEntry[]): void => {
        entries.forEach(entry => {
            const element = entry.target as HTMLElement;
            if (!this.observedElements.has(element)) {
                return;
            }

            const overflowing = entry.isIntersecting && Math.round(entry.rootBounds!.right) < Math.round(entry.boundingClientRect.right);
            this.observedElements.set(element, overflowing);
        });
    };

    private overflowDetected = (element: HTMLElement): boolean => {
        return !!this.observedElements.get(element);
    };
}
